import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IconArrowForward,
  IconCircleCheck,
  IconSearch,
  IconUserShare,
  IconChevronRight,
  IconClockPause,
  IconPlayerPlay,
} from '@tabler/icons-react';
import './BoardChatPreAtendimentoCloseModal.css';

const FECHAR_SEM_TECNICO_TIP =
  'Não há técnico vinculado a este agente de chat, primeiramente é necessário este vinculo.';

function agentDisplayName(agent) {
  return (
    String(agent?.name || agent?.available_name || agent?.email || '').trim() ||
    `Agente ${agent?.id ?? ''}`
  );
}

function agentInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

export function BoardChatOsChipMenu({
  open,
  anchorRect,
  osId,
  status,
  trackable = true,
  unclaimed = false,
  /** Conversa ainda não assumida — só Consultar. */
  consultOnly = false,
  claimBusy = false,
  agentCodtech = '',
  handoffAgents = [],
  handoffBusy = false,
  tipProps,
  onClose,
  onAssumir,
  onConsultar,
  onEncaminhar,
  onFechar,
  onRepassar,
  onAdiar,
  onRetomar,
  retomarBlocked = false,
  retomarBlockedTip = '',
}) {
  const [handoffOpen, setHandoffOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setHandoffOpen(false);
      return undefined;
    }
    const onDoc = (e) => {
      if (e.target?.closest?.('.bch-os-chip-menu')) return;
      if (e.target?.closest?.('.bch-os-chip')) return;
      if (e.target?.closest?.('.bch-pre-os-btn')) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    // Adia o bind para não fechar no mesmo clique que abre o menu.
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onDoc, true);
    }, 0);
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setHandoffOpen(false);
  }, [open, osId]);

  if (!open || !anchorRect) return null;

  const statusNorm = String(status || 'pending').toLowerCase();
  const isPending = !statusNorm || statusNorm === 'pending';
  const isSnoozed = statusNorm === 'snoozed';
  const canAct = (isPending || isSnoozed) && trackable !== false;
  const hasTech = Boolean(String(agentCodtech || '').trim());
  const others = Array.isArray(handoffAgents) ? handoffAgents : [];
  const canHandoff = canAct && others.length > 0;
  const menuWidth = handoffOpen ? 220 : unclaimed && !consultOnly ? 168 : 156;
  const left = Math.min(
    Math.max(8, anchorRect.left),
    window.innerWidth - (menuWidth + 8)
  );
  const top = Math.min(anchorRect.bottom + 4, window.innerHeight - 8);

  if (consultOnly) {
    return createPortal(
      <div
        className="bch-os-chip-menu"
        role="menu"
        aria-label={`Consultar OS ${osId}`}
        style={{ left, top, minWidth: menuWidth }}
      >
        <button
          type="button"
          role="menuitem"
          className="bch-os-chip-menu-item"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onConsultar?.();
          }}
        >
          <IconSearch size={12} stroke={1.7} aria-hidden />
          Consultar
        </button>
      </div>,
      document.body
    );
  }

  if (unclaimed) {
    return createPortal(
      <div
        className="bch-os-chip-menu"
        role="menu"
        aria-label={`Assumir OS ${osId}`}
        style={{ left, top, minWidth: menuWidth }}
      >
        <button
          type="button"
          role="menuitem"
          className="bch-os-chip-menu-item"
          disabled={claimBusy}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (claimBusy) return;
            onAssumir?.();
          }}
        >
          <IconUserShare size={12} stroke={1.7} aria-hidden />
          {claimBusy ? 'Assumindo…' : 'Assumir atendimento'}
        </button>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      className={`bch-os-chip-menu${handoffOpen ? ' is-handoff' : ''}`}
      role="menu"
      aria-label={`Ações da OS ${osId}`}
      style={{ left, top, minWidth: menuWidth }}
    >
      <button
        type="button"
        role="menuitem"
        className="bch-os-chip-menu-item"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onConsultar?.();
        }}
      >
        <IconSearch size={12} stroke={1.7} aria-hidden />
        Consultar
      </button>
      {canAct ? (
        <>
          <button
            type="button"
            role="menuitem"
            className="bch-os-chip-menu-item"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEncaminhar?.();
            }}
          >
            <IconArrowForward size={12} stroke={1.7} aria-hidden />
            Encaminhar OS
          </button>
          {canHandoff ? (
            <>
              <button
                type="button"
                role="menuitem"
                className={`bch-os-chip-menu-item${handoffOpen ? ' is-active' : ''}`}
                aria-expanded={handoffOpen}
                aria-haspopup="menu"
                disabled={handoffBusy}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setHandoffOpen((v) => !v);
                }}
              >
                <IconUserShare size={12} stroke={1.7} aria-hidden />
                <span className="bch-os-chip-menu-item-label">Repassar</span>
                <IconChevronRight
                  size={12}
                  stroke={1.7}
                  className={`bch-os-chip-menu-chevron${handoffOpen ? ' is-open' : ''}`}
                  aria-hidden
                />
              </button>
              {handoffOpen ? (
                <div
                  className="bch-os-chip-handoff-list"
                  role="menu"
                  aria-label="Repassar para atendente"
                >
                  {others.map((agent) => {
                    const id = Number(agent?.id ?? agent?.user_id);
                    const name = agentDisplayName(agent);
                    return (
                      <button
                        key={id}
                        type="button"
                        role="menuitem"
                        className="bch-os-chip-menu-item bch-os-chip-handoff-item"
                        disabled={handoffBusy || !Number.isFinite(id)}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!Number.isFinite(id) || handoffBusy) return;
                          onRepassar?.(id, name);
                        }}
                      >
                        <span className="bch-os-chip-handoff-avatar" aria-hidden>
                          {agentInitials(name)}
                        </span>
                        <span className="bch-os-chip-handoff-name">{name}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </>
          ) : null}
          {isPending ? (
            <button
              type="button"
              role="menuitem"
              className="bch-os-chip-menu-item"
              disabled={handoffBusy}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (handoffBusy) return;
                onAdiar?.();
              }}
            >
              <IconClockPause size={12} stroke={1.7} aria-hidden />
              Adiar
            </button>
          ) : null}
          {isSnoozed ? (
            <span
              className="bch-os-chip-menu-item-wrap"
              {...(typeof tipProps === 'function' && retomarBlocked && retomarBlockedTip
                ? tipProps(retomarBlockedTip, 'above')
                : {})}
            >
              <button
                type="button"
                role="menuitem"
                className={`bch-os-chip-menu-item${retomarBlocked ? ' is-disabled' : ''}`}
                disabled={handoffBusy || retomarBlocked}
                aria-disabled={retomarBlocked}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (handoffBusy || retomarBlocked) return;
                  onRetomar?.();
                }}
              >
                <IconPlayerPlay size={12} stroke={1.7} aria-hidden />
                Retomar
              </button>
            </span>
          ) : null}
          <span
            className="bch-os-chip-menu-item-wrap"
            {...(typeof tipProps === 'function' && !hasTech
              ? tipProps(FECHAR_SEM_TECNICO_TIP, 'above')
              : {})}
          >
            <button
              type="button"
              role="menuitem"
              className={`bch-os-chip-menu-item${!hasTech ? ' is-disabled' : ''}`}
              disabled={!hasTech}
              aria-disabled={!hasTech}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!hasTech) return;
                onFechar?.();
              }}
            >
              <IconCircleCheck size={12} stroke={1.7} aria-hidden />
              Fechar OS
            </button>
          </span>
        </>
      ) : trackable === false ? (
        <p className="bch-os-chip-menu-hint">OS sem vínculo rastreável — só consulta</p>
      ) : null}
    </div>,
    document.body
  );
}
